import { useEffect, useState, useRef } from "react";
import { EVM_TOKENS, SOL_TOKENS } from "@/data/tokens";
import TokenIcon from "@/components/TokenIcon";
import { fmtAmount, fmtNum } from "@/lib/wallet";
import { ArrowDownToLine, ArrowUpFromLine, Sparkles, Activity } from "lucide-react";

const HANDLES = [
  "0xWhaleKing", "validator.eth", "sol.maxi", "deg3n_alpha", "yield.king",
  "mev_searcher", "stakemaster", "polygon.legend", "ape_in_3000", "the.fund",
  "node.runner", "compound.chad", "lp.ninja", "0xAnonStaker", "based_validator",
  "rocketpool.dao", "bull.in.china", "anon.eth", "satoshi.jr", "based.dev",
];

function randAddr(chain) {
  const hex = "0123456789abcdef";
  const b58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
  if (chain === "evm") {
    let s = "0x"; for (let i = 0; i < 8; i++) s += hex[Math.floor(Math.random() * 16)];
    return s + "...";
  }
  let s = ""; for (let i = 0; i < 6; i++) s += b58[Math.floor(Math.random() * b58.length)];
  return s + "...";
}

function makeEvent() {
  const chain = Math.random() < 0.55 ? "evm" : "solana";
  const list = chain === "evm" ? EVM_TOKENS : SOL_TOKENS;
  const tok = list[Math.floor(Math.random() * list.length)];
  const useBoost = Math.random() < 0.25;
  const r = Math.random();
  const type = r < 0.62 ? "stake" : r < 0.85 ? "unstake" : "claim";
  const handle = HANDLES[Math.floor(Math.random() * HANDLES.length)];
  const useAddr = Math.random() < 0.5;
  const mult = 1 + Math.random() * 90;
  const amount = +(tok.minStake * mult).toFixed(tok.minStake < 0.01 ? 6 : tok.minStake < 1 ? 4 : 2);

  return {
    id: "ev_" + Math.random().toString(36).slice(2, 10),
    type, chain, token: tok.symbol, color: tok.color, amount,
    apr: useBoost ? tok.boostApr : tok.apr,
    pool: useBoost ? "boost" : "standard",
    actor: useAddr ? randAddr(chain) : "@" + handle,
    t: Date.now(),
  };
}

const TYPE_META = {
  stake:   { label: "STAKE",   accent: "var(--success)", icon: ArrowDownToLine, sign: "+" },
  unstake: { label: "UNSTAKE", accent: "var(--warning)", icon: ArrowUpFromLine, sign: "−" },
  claim:   { label: "CLAIM",   accent: "#7dd3fc",        icon: Sparkles,        sign: "↻" },
};

function elapsed(ms) {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60);
  if (m < 60) return m + "m";
  return Math.floor(m / 60) + "h";
}

export default function LiveActivity() {
  const [events, setEvents] = useState(() => {
    const arr = [];
    for (let i = 0; i < 6; i++) {
      const ev = makeEvent();
      ev.t = Date.now() - (i * 1000 * (3 + Math.random() * 8));
      arr.push(ev);
    }
    return arr;
  });
  const [, force] = useState(0);
  const [stats, setStats] = useState({ count: 18429, volume: 28_400_000 });
  const counterRef = useRef(null);

  useEffect(() => {
    const push = () => {
      setEvents(prev => [makeEvent(), ...prev].slice(0, 8));
      setStats(s => ({
        count: s.count + 1,
        volume: s.volume + Math.round(50000 + Math.random() * 240000),
      }));
    };
    const t1 = setInterval(push, 2400 + Math.random() * 1400);
    const t2 = setInterval(() => force(x => x + 1), 4000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  return (
    <section className="relative overflow-hidden border-y" style={{ borderColor: "var(--border)" }}>
      {/* Background mesh */}
      <div className="absolute inset-0 -z-10 mesh-hero opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-24">
        <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 items-start">
          {/* Left side: heading + counters */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 h-7 border rounded-full mb-4 sm:mb-5" style={{ borderColor: "var(--border-strong)", background: "rgba(255,255,255,0.02)" }} data-testid="live-indicator">
              <Activity size={11} style={{ color: "var(--success)" }} />
              <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: "var(--success)" }} />
              <span className="text-[10px] uppercase tracking-[0.25em] font-mono">LIVE · 24/7</span>
            </div>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter leading-[0.95]">
              <span className="block">Stakes happening</span>
              <span className="grad-sol-text">right now.</span>
            </h2>
            <p className="mt-4 sm:mt-5 text-sm max-w-md" style={{ color: "var(--text-2)" }}>
              Every position open, claim, and exit streams here in real time, the moment it lands in the validator mempool.
            </p>

            {/* Counters */}
            <div className="grid grid-cols-2 gap-3 mt-6 sm:mt-8 max-w-md">
              <div className="border p-4" style={{ borderColor: "var(--border)", background: "rgba(20,241,149,0.03)" }}>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>Tx · 24h</div>
                <div className="font-mono font-bold text-xl sm:text-2xl mt-1 tabular-nums" ref={counterRef}>{stats.count.toLocaleString("en-US")}</div>
                <div className="text-[10px] mt-1" style={{ color: "var(--success)" }}>+{Math.floor(stats.count * 0.012).toLocaleString("en-US")} / hour</div>
              </div>
              <div className="border p-4" style={{ borderColor: "var(--border)", background: "rgba(0,85,255,0.03)" }}>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>Volume · 24h</div>
                <div className="font-mono font-bold text-xl sm:text-2xl mt-1">${fmtNum(stats.volume, 2)}</div>
                <div className="text-[10px] mt-1" style={{ color: "var(--success)" }}>+{(stats.volume * 0.034 / 1e6).toFixed(2)}M / hour</div>
              </div>
            </div>
          </div>

          {/* Right side: animated card stream */}
          <div className="lg:col-span-7 relative" data-testid="live-activity-feed">
            <div
              className="absolute top-0 left-0 right-0 h-12 z-10 pointer-events-none"
              style={{ background: "linear-gradient(180deg, var(--void) 0%, transparent 100%)" }}
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-12 z-10 pointer-events-none"
              style={{ background: "linear-gradient(0deg, var(--void) 0%, transparent 100%)" }}
            />
            <div className="flex flex-col gap-3 max-h-[500px] overflow-hidden">
              {events.map((ev) => {
                const M = TYPE_META[ev.type];
                const Icon = M.icon;
                const accentBorder = ev.chain === "evm" ? "var(--evm-grad)" : "var(--sol-grad)";
                const isBoost = ev.pool === "boost";
                return (
                  <article
                    key={ev.id}
                    data-testid={`activity-row-${ev.id}`}
                    className="slide-in-right relative grid grid-cols-12 items-center gap-3 p-4 border"
                    style={{
                      background: "var(--surface-1)",
                      borderColor: "var(--border)",
                      backgroundImage: ev.chain === "evm"
                        ? "linear-gradient(135deg, rgba(0,85,255,0.05) 0%, transparent 60%)"
                        : "linear-gradient(135deg, rgba(153,69,255,0.06) 0%, transparent 60%)",
                    }}
                  >
                    {/* Left gradient bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundImage: accentBorder }} />

                    {/* Icon column */}
                    <div className="col-span-1 flex items-center justify-center">
                      <TokenIcon symbol={ev.token} color={ev.color} size="md" />
                    </div>

                    {/* Asset + chain */}
                    <div className="col-span-3">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-extrabold text-base tracking-tight">{ev.token}</span>
                        {isBoost && (
                          <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 grad-boost-bg text-black rounded-sm">24H</span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest font-mono"
                        style={{ color: ev.chain === "evm" ? "#5a8fff" : "#b86bff" }}>
                        {ev.chain} · {ev.apr.toFixed(1)}% APR
                      </div>
                    </div>

                    {/* Action label + actor */}
                    <div className="col-span-3">
                      <div className="inline-flex items-center gap-1.5">
                        <Icon size={11} style={{ color: M.accent }} />
                        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: M.accent }}>{M.label}</span>
                      </div>
                      <div className="font-mono text-xs mt-0.5" style={{ color: "var(--text-3)" }}>{ev.actor}</div>
                    </div>

                    {/* Amount */}
                    <div className="col-span-3 text-right">
                      <div className="font-mono font-bold text-lg">
                        <span style={{ color: M.accent }}>{M.sign}</span>
                        {fmtAmount(ev.amount)}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: "var(--text-3)" }}>{ev.token}</div>
                    </div>

                    {/* Time */}
                    <div className="col-span-2 text-right">
                      <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>Block</div>
                      <div className="font-mono text-sm">{elapsed(ev.t)} ago</div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
